import DOMPurify from 'dompurify';
import parse, {
	attributesToProps,
	domToReact,
	type DOMNode,
	type HTMLReactParserOptions,
} from 'html-react-parser';

import type { QuoteVariations, TableVariations } from '@/_types/Blocks/Editor';
import type { WysiwygClassesType } from '@/_types/Shared/Common';

// import { Link } from '@inertiajs/react';

import { Button } from './Button/Button';

declare const home_url: string;

type Props = {
	context?: string;
	html?: string;
	sanitize?: boolean;
	quoteStyle?: QuoteVariations;
	tableStyle?: TableVariations;
	className?: WysiwygClassesType;
};

/**
 * Html Component
 *
 * This component is responsible for rendering HTML content while ensuring
 * security through sanitization using DOMPurify.
 *
 * @component
 * @param {Object} props - The props of the Html component.
 * @param {string} props.html - The HTML content to be rendered.
 * @returns {string | JSX.Element | JSX.Element[]} - The parsed HTML content as a string,
 * JSX element, or an array of JSX elements. */

const Html = ({
	context = '',
	html,
	sanitize,
	quoteStyle = 'classic',
	tableStyle = 'classic',
	className = {
		p: '',
		h1: '',
		h2: '',
		h3: '',
		h4: '',
		h5: '',
		h6: '',
		li: '',
		a: {
			style: 'a',
			btnDecorationElement: 'underline',
			btnHasIcon: false,
			aClassname: '',
		},
		btnPrimary: {
			style: 'primary',
			btnDecorationElement: 'short',
			btnHasIcon: false,
			primaryClassname: '',
			animation: 'none'
		},
		btnSecondary: {
			style: 'secondary',
			btnDecorationElement: 'short',
			btnHasIcon: false,
			secondaryClassname: '',
			animation: 'none'
		},
		btnTertiary: {
			style: 'tertiary',
			btnDecorationElement: 'short',
			btnHasIcon: false,
			tertiaryClassname: '',
			animation: 'none'
		},
	},
}: Props): string | JSX.Element | JSX.Element[] => {
	if (!html) return '';

	// Sanitize the HTML content using DOMPurify to prevent XSS attacks.
	const sanitizedHtml = sanitize ? DOMPurify.sanitize(html) : html;

	const options: HTMLReactParserOptions = {
		replace(domNode: any) {
			if (context) {
				if (domNode.type === 'tag' && domNode.name === 'img' && domNode.attribs) {
					let itemClass = '';
					let divClass = '';
					let align = '';
					if (
						domNode.prev &&
						domNode.prev.dataForPrev &&
						domNode.prev.dataForPrev.includes('[caption')
					) {
						const data = domNode.prev.dataForPrev;
						align = data.replace(/caption.*align="/, '');
						align = align.replace(/".*"]/, '');
						align = align.replace('[', '');
						align = align.trim();
					}

					if (domNode.attribs.class.includes('aligncenter')) {
						domNode.attribs.class.replace('aligncenter', '');
						itemClass = domNode.attribs.class + ' mx-auto';
					} else if (domNode.attribs.class.includes('alignright')) {
						domNode.attribs.class.replace('alignright', '');
						itemClass = domNode.attribs.class + ' ml-8 float-right';
					} else if (domNode.attribs.class.includes('alignleft')) {
						domNode.attribs.class.replace('alignleft', '');
						itemClass = domNode.attribs.class + ' mr-8 float-left';
					} else {
						itemClass = domNode.attribs.class;
					}

					if (align == 'aligncenter') {
						divClass = 'w-fit mx-auto';
					} else if (align == 'alignright') {
						divClass = 'w-fit ml-8 float-right';
					} else if (align == 'alignleft') {
						divClass = 'w-fit mr-8 float-left';
					} else {
						divClass = 'w-fit';
					}

					if (domNode.next && domNode.next.data.includes('[/caption]')) {
						const data = domNode.next.data.replace('[/caption]', '');
						return (
							<div className={divClass}>
								<img
									alt=''
									className={domNode.attribs.class}
									src={domNode.attribs.src}
									width={domNode.attribs.width}
									height={domNode.attribs.height}
								/>
								<p className={className.legende}>{data}</p>
							</div>
						);
					} else {
						return (
							<img
								alt=''
								className={itemClass}
								src={domNode.attribs.src}
								width={domNode.attribs.width}
								height={domNode.attribs.height}
							/>
						);
					}
				}

				if (domNode.type === 'text' && !domNode.name && !domNode.attribs) {
					if (domNode.data.includes('[caption')) {
						domNode.dataForPrev = domNode.data;
						domNode.data = '';
					}
					if (domNode.data.includes('[/caption]')) {
						domNode.data = '';
					}
					if (domNode.data.includes('[video')) {
						let data = domNode.data;
						data = data.replace(/video.*mp4="/, '');
						data = data.replace('[', '');
						data = data.replace('"][/video]', '');
						data = data.trim();

						// let width = domNode.data;
						// width = width.replace(/video.*width="/, '');
						// width = width.replace("[", "");
						// width = width.replace(/" height.*video]/, '');
						// width = width.trim();

						// let height = domNode.data;
						// height = height.replace(/video.*height="/, '');
						// height = height.replace("[", "");
						// height = height.replace(/" mp4=".*video]/, '');
						// height = height.trim();

						return (
							// eslint-disable-next-line jsx-a11y/media-has-caption
							<video controls>
								<source src={data} type='video/mp4'></source>
							</video>
						);
					}
					if (domNode.data.includes('[audio')) {
						let data = domNode.data;
						data = data.replace(/audio.*mp3="/, '');
						data = data.replace('[', '');
						data = data.replace('"][/audio]', '');
						data = data.trim();

						return (
							// eslint-disable-next-line jsx-a11y/media-has-caption
							<audio controls>
								<source src={data} type='video/mp4'></source>
							</audio>
						);
					}
				}

				if (domNode.type === 'tag' && domNode.name === 'blockquote') {
					domNode.attribs['data-quote'] = quoteStyle;
				}

				if (domNode.type === 'tag' && domNode.name === 'table') {
					domNode.attribs['data-table'] = tableStyle;
				}

				// BTN
				if (domNode.type === 'tag' && domNode.name === 'a') {
					const props = attributesToProps(domNode.attribs);

					if (Object.prototype.hasOwnProperty.call(props, 'type')) {
						if (props.type) {
							if (props.type === 'primary') {
								const label = (domNode.children[0] as any)?.data;
								const parentAttribs = domNode.parent as any;
								let target = false;

								if (props.target === '_blank') {
									target = true;
								}

								return (
									<Button
										style='primary'
										btnHasIcon={className.btnPrimary?.btnHasIcon}
										btnDecorationElement={className.btnPrimary?.btnDecorationElement}
										primaryClassname={className.btnPrimary?.primaryClassname}
										label={label}
										url={domNode.attribs.href}
										urlType={checkExternalUrl(domNode.children[0])}
										className={parentAttribs?.attribs.style}
										urlOpenNewTab={target}

										animation={className.btnPrimary?.animation}
									/>
								);
							} else if (props.type === 'secondary') {
								const label = (domNode.children[0] as any)?.data;
								const parentAttribs = domNode.parent as any;
								let target = false;

								if (props.target === '_blank') {
									target = true;
								}

								return (
									<Button
										style='secondary'
										btnHasIcon={className.btnSecondary?.btnHasIcon}
										btnDecorationElement={className.btnSecondary?.btnDecorationElement}
										secondaryClassname={className.btnSecondary?.secondaryClassname}
										label={label}
										url={domNode.attribs.href}
										urlType={checkExternalUrl(domNode.children[0])}
										className={parentAttribs?.attribs.style}
										urlOpenNewTab={target}

										animation={className.btnSecondary?.animation}
									/>
								);
							} else if (props.type === 'tertiary') {
								const label = (domNode.children[0] as any)?.data;
								const parentAttribs = domNode.parent as any;
								let target = false;

								if (props.target === '_blank') {
									target = true;
								}

								return (
									<Button
										style='tertiary'
										btnHasIcon={className.btnTertiary?.btnHasIcon}
										btnDecorationElement={className.btnTertiary?.btnDecorationElement}
										tertiaryClassname={className.btnTertiary?.tertiaryClassname}
										label={label}
										url={domNode.attribs.href}
										urlType={checkExternalUrl(domNode.children[0])}
										className={parentAttribs?.attribs.style}
										urlOpenNewTab={target}

										animation={className.btnTertiary?.animation}
									/>
								);
							} else if (props.type === 'a') {
								const label = (domNode.children[0] as any)?.data;
								return (
									<Button
										style='a'
										btnHasIcon={className.a?.btnHasIcon}
										btnDecorationElement={className.a?.btnDecorationElement}
										aClassname={className.a?.aClassname}
										label={label}
										url={domNode.attribs.href}
										urlType={checkExternalUrl(domNode.children[0])}
									/>
								);
							}
						}
					}

					// const testUrl = checkExternalUrl(domNode.children[0]);

					// if (testUrl === 'link') {
					// 	return <a {...props} className={`${className.a?.aClassname} ${className.a?.btnDecorationElement}`}>{domToReact(domNode.children as DOMNode[], options)}</a>;
					// } else {
					// 	return (
					// 		<Link href={props.href as string} className={`${className.a?.aClassname} ${className.a?.btnDecorationElement}`}>{domToReact(domNode.children as DOMNode[], options)}</Link>
					// 	);
					// }
				}

				// FontStyle
				if (domNode.type === 'tag' && domNode.name === 'p' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.p ? className.p : '';
					return <p {...props}>{domToReact(domNode.children as DOMNode[], options)}</p>;
				}
				/* TODO add bullet point + counter color */
				if (domNode.type === 'tag' && domNode.name === 'li') {
					const props = attributesToProps(domNode.attribs);
					props.className = className.li ? className.li : '';
					return <li {...props}>{domToReact(domNode.children as DOMNode[], options)}</li>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h1' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h1 ? className.h1 : '';
					return <h1 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h1>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h2' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h2 ? className.h2 : '';
					return <h2 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h2>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h3' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h3 ? className.h3 : '';
					return <h3 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h3>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h4' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h4 ? className.h4 : '';
					return <h4 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h4>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h5' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h5 ? className.h5 : '';
					return <h5 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h5>;
				}
				if (domNode.type === 'tag' && domNode.name === 'h6' && !domNode.parent) {
					const props = attributesToProps(domNode.attribs);
					props.className = className.h6 ? className.h6 : '';
					return <h6 {...props}>{domToReact(domNode.children as DOMNode[], options)}</h6>;
				}
			}
		},
	};

	// Parse the sanitized HTML using html-react-parser.
	const parsedHtml = parse(sanitizedHtml, options);

	// const parsedHtml = parse(sanitizedHtml, options);

	// Return the React node representing the sanitized and parsed HTML.
	return <div className={`${className.p ? className.p : ''}`}>{parsedHtml}</div>;
};

export default Html;

const checkExternalUrl = (link: any) => {
	const url = link.parent.attribs.href;

	if (!url) {
		return 'link';
	}

	if (url.includes(home_url)) {
		return 'internal';
	}

	return 'link';
};
